import { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useCart } from '../context/CartContext.jsx'
import { useCustomer } from '../context/CustomerContext.jsx'
import { useProducts } from '../context/ProductsContext.jsx'
import { Button } from './primitives/index.js'
import { NAV_LINKS } from '../config/site.js'
import './Nav.css'

export default function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { count, setIsOpen } = useCart()
  const { isAuthenticated } = useCustomer()
  const { siteContent, branding, customerAccounts } = useProducts()
  const accountsEnabled = customerAccounts?.enabled !== false

  const navCfg = siteContent?.nav ?? {}
  // Admin-configured links come pre-shaped as {label, to|url|href}; fall back
  // to the static default set (see config/site.js) when nothing's set yet.
  const links = navCfg.links?.length ? navCfg.links : NAV_LINKS
  const ctaText = navCfg.ctaText ?? 'Shop Now →'
  const ctaLink = navCfg.ctaLink ?? '/catalog'

  // Holiday logo takes priority over the white logo; both fall back to the static file.
  // The static file is a dark logotype meant for light backgrounds, so invert it here
  // (a dark nav bar) when no admin-configured logo is set.
  const usingFallbackLogo = !branding?.logoHoliday?.url && !branding?.logoWhite?.url
  const logoSrc = branding?.logoHoliday?.url ?? branding?.logoWhite?.url ?? '/logos/lookslab-logo.png'

  return (
    <header className="nav">
      <div className="container nav__inner">
        <Link to="/" className="nav__logo">
          <img
            src={logoSrc}
            alt="LooksLab"
            className="nav__logo-img"
            width={106}
            height={52}
            style={usingFallbackLogo ? { filter: 'invert(1)' } : undefined}
          />
        </Link>

        <nav className="nav__links">
          {links.map((link) => {
            const to = link.to ?? link.url ?? link.href
            return (
              <NavLink key={to} to={to} className={({ isActive }) => `nav__link ${isActive ? 'is-active' : ''}`}>
                {link.label}
              </NavLink>
            )
          })}
        </nav>

        <div className="nav__actions">
          <Link to="/catalog" className="nav__icon-btn nav__search-btn" aria-label="Search peptides">
            <SearchIcon />
          </Link>
          {accountsEnabled && (
            <Link to={isAuthenticated ? '/account' : '/account/login'} className="nav__icon-btn" aria-label="Account">
              <UserIcon />
            </Link>
          )}
          <button
            className="nav__icon-btn nav__cart-btn"
            onClick={() => setIsOpen(true)}
            aria-label={`Open cart, ${count} item${count === 1 ? '' : 's'}`}
          >
            <CartIcon />
            <motion.span className="nav__cart-count" aria-hidden="true" initial={{ scale: 0 }} animate={{ scale: 1 }}>
              {count}
            </motion.span>
          </button>
          <Button to={ctaLink} variant="primary" size="sm" className="nav__shop-cta">
            {ctaText}
          </Button>
          <button className="nav__icon-btn nav__menu-btn" onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle menu">
            <MenuIcon open={mobileOpen} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="nav__mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          >
            {links.map((link) => {
              const to = link.to ?? link.url ?? link.href
              return (
                <NavLink key={to} to={to} className="nav__mobile-link" onClick={() => setMobileOpen(false)}>
                  {link.label}
                </NavLink>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

function SearchIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  )
}

function CartIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  )
}

function UserIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

function MenuIcon({ open }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      {open ? <path d="M18 6 6 18M6 6l12 12" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
    </svg>
  )
}
